import { useCallback, useId, useState } from 'react';
import { PlugZap } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Dialog } from '../ui/Dialog/Dialog';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { TokenRevealDialog } from '../cameraSources/TokenRevealDialog';
import { Section } from '../templates/Section/Section';
import { buildApiUrl } from '../../lib/apiClient';
import { useToast } from '../../stores/toastStore';
import { controllerIngestPath, type ControllerSource, type ControllerSourceWithToken } from '../../api/controller';
import { useCreateControllerSource, useDeleteControllerSource, useRevokeControllerSource } from '../../hooks/useController';
import { formatDateTime, isStale } from './controllerFormat';
import './Controller.css';

/** Header the bridge sends the device token in (backend sensorDeviceTokenAuth). */
export const DEVICE_TOKEN_HEADER = 'X-Device-Token';

/** Absolute ingest URL of a source. */
export function controllerIngestUrl(sourceId: string): string {
  const path = buildApiUrl(controllerIngestPath(sourceId));
  try {
    return new URL(path, window.location.origin).toString();
  } catch {
    return path;
  }
}

function CreateSourceDialog({
  isOpen,
  onCancel,
  onSubmit,
  isSubmitting,
  submitError,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (name: string) => void;
  isSubmitting: boolean;
  submitError: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [name, setName] = useState('Home Assistant');
  const [touched, setTouched] = useState(false);
  const nameError = touched && name.trim() === '' ? t('controller.sources.nameError') : undefined;

  return (
    <Dialog isOpen={isOpen} onClose={onCancel} title={t('controller.sources.createTitle')} size="sm">
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (name.trim() !== '' && !isSubmitting) onSubmit(name.trim());
        }}
      >
        <Stack gap="4">
          <Input
            id={`${id}-name`}
            label={t('controller.sources.nameLabel')}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            error={nameError}
            required
            fullWidth
          />
          {submitError && <Alert>{t('controller.sources.createError')}</Alert>}
          <Inline justify="end" gap="2">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {t('controller.common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} aria-busy={isSubmitting}>
              {t('controller.sources.create')}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Dialog>
  );
}

function SourceCard({ source, onRevoke, onDelete }: { source: ControllerSource; onRevoke: () => void; onDelete: () => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const revoked = source.status === 'revoked';
  const stale = !revoked && isStale(source.lastReceivedAt);

  return (
    <Card variant="outlined">
      <Stack gap="3">
        <Inline justify="between" align="center" gap="2">
          <Heading level={3}>{source.name}</Heading>
          <Badge tone={revoked ? 'neutral' : 'success'}>{t(`controller.sources.status.${source.status}`)}</Badge>
        </Inline>
        <Text color="muted">
          {source.lastReceivedAt
            ? t('controller.sources.lastReceived', { time: formatDateTime(source.lastReceivedAt, language) })
            : t('controller.sources.neverReceived')}
        </Text>
        {stale && (
          <Alert tone="warning" live="off" compact>
            {t('controller.sources.stale')}
          </Alert>
        )}
        <Inline gap="2">
          {revoked ? (
            <Button variant="secondary" onClick={onDelete}>
              {t('controller.sources.delete')}
            </Button>
          ) : (
            <Button variant="danger" onClick={onRevoke}>
              {t('controller.sources.revoke')}
            </Button>
          )}
        </Inline>
      </Stack>
    </Card>
  );
}

export interface ControllerSourcesProps {
  aquariumId: string;
  sources: readonly ControllerSource[];
}

/** The aquarium's controller sources: add (one-time token), revoke, delete. */
export function ControllerSources({ aquariumId, sources }: ControllerSourcesProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const create = useCreateControllerSource(aquariumId);
  const revoke = useRevokeControllerSource(aquariumId);
  const remove = useDeleteControllerSource(aquariumId);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ControllerSourceWithToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ControllerSource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ControllerSource | null>(null);

  const openCreate = () => {
    create.reset();
    setCreating(true);
  };
  // Stable identity: ConfirmDialog re-runs its focus effect when onCancel changes.
  const cancelRevoke = useCallback(() => setRevokeTarget(null), []);
  const cancelDelete = useCallback(() => setDeleteTarget(null), []);

  return (
    <Section
      title={t('controller.sources.title')}
      description={t('controller.sources.description')}
      actions={
        sources.length > 0 ? (
          <Button variant="secondary" onClick={openCreate}>
            {t('controller.sources.add')}
          </Button>
        ) : undefined
      }
    >
      {sources.length === 0 ? (
        <Card>
          <EmptyState
            icon={<PlugZap aria-hidden="true" />}
            headline={t('controller.sources.emptyTitle')}
            description={t('controller.sources.emptyDescription')}
            headingLevel={3}
            actionLabel={t('controller.sources.add')}
            onAction={openCreate}
          />
        </Card>
      ) : (
        <ul className="controller-sources">
          {sources.map((source) => (
            <li key={source.id}>
              <SourceCard source={source} onRevoke={() => setRevokeTarget(source)} onDelete={() => setDeleteTarget(source)} />
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateSourceDialog
          isOpen
          onCancel={() => setCreating(false)}
          isSubmitting={create.isPending}
          submitError={create.isError}
          onSubmit={(name) =>
            create.mutate(name, {
              onSuccess: (source) => {
                setCreating(false);
                setCreated(source);
              },
            })
          }
        />
      )}

      {created && (
        <TokenRevealDialog
          isOpen
          title={t('controller.sources.tokenTitle')}
          description={t('controller.sources.tokenDescription')}
          endpointUrl={controllerIngestUrl(created.id)}
          headerName={DEVICE_TOKEN_HEADER}
          token={created.token}
          onConfirm={() => {
            setCreated(null);
            showToast(t('controller.sources.created'), 'success');
          }}
        />
      )}

      <ConfirmDialog
        isOpen={revokeTarget !== null}
        title={t('controller.sources.revokeTitle')}
        message={t('controller.sources.revokeMessage', { name: revokeTarget?.name ?? '' })}
        cancelLabel={t('controller.common.cancel')}
        confirmLabel={t('controller.sources.revoke')}
        isConfirming={revoke.isPending}
        onCancel={cancelRevoke}
        onConfirm={() => {
          if (!revokeTarget) return;
          revoke.mutate(revokeTarget.id, {
            onSuccess: () => {
              setRevokeTarget(null);
              showToast(t('controller.sources.revoked'), 'success');
            },
            onError: () => showToast(t('controller.common.saveError'), 'error'),
          });
        }}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={t('controller.sources.deleteTitle')}
        message={t('controller.sources.deleteMessage', { name: deleteTarget?.name ?? '' })}
        cancelLabel={t('controller.common.cancel')}
        confirmLabel={t('controller.sources.delete')}
        isConfirming={remove.isPending}
        onCancel={cancelDelete}
        onConfirm={() => {
          if (!deleteTarget) return;
          remove.mutate(deleteTarget.id, {
            onSuccess: () => {
              setDeleteTarget(null);
              showToast(t('controller.sources.deleted'), 'success');
            },
            onError: () => showToast(t('controller.common.saveError'), 'error'),
          });
        }}
      />
    </Section>
  );
}
